import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { FontIcon } from '@fluentui/react';
import {
  FilterType,
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import {
  deleteAccessRule,
  getAccessRules,
  type AccessRuleEntity,
  type IAccessRule,
} from '../../services/accessRuleService';

interface AccessRulesListProps {
  entity: AccessRuleEntity;
}

const stringFilter = { enabled: true as const, type: FilterType.String };

const summarizePermissions = (rule: IAccessRule): string => {
  const perms = rule.permissions ?? [];
  if (perms.length === 0) return '—';
  let users = 0;
  let groups = 0;
  for (const p of perms) {
    if (p.userId != null) users++;
    else if (p.groupId != null) groups++;
  }
  const parts: string[] = [];
  if (users > 0) parts.push(`${users} user${users === 1 ? '' : 's'}`);
  if (groups > 0) parts.push(`${groups} group${groups === 1 ? '' : 's'}`);
  return `${perms.length} (${parts.join(', ')})`;
};

const COLUMNS: DataGridColumn<IAccessRule>[] = [
  { key: 'id', name: 'Id', width: 80 },
  { key: 'metadataName', name: 'Metadata', filter: stringFilter },
  { key: 'fieldName', name: 'Field', filter: stringFilter },
  { key: 'operator', name: 'Operator', width: 110, filter: stringFilter },
  { key: 'fieldValue', name: 'Value', filter: stringFilter },
  {
    key: 'permissions',
    name: 'Permissions',
    width: 160,
    renderCell: summarizePermissions,
  },
  { key: 'tenantId', name: 'Tenant', width: 90 },
];

const editPath = (entity: AccessRuleEntity, id: number | string): string =>
  `/edit${entity}Access?id=${id}`;
const newPath = (entity: AccessRuleEntity): string => `/new${entity}Access`;

const AccessRulesList = ({ entity }: AccessRulesListProps) => {
  const profileData = useAtomValue(profileDataAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const navigate = useNavigate();
  const gridRef = useRef<IPagedDataGridRefType>(null);

  const [selectedId, setSelectedId] = useState<number>();
  const isAdmin = profileData.roles.includes(appRoles.Admin);

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'new',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate(newPath(entity)),
        disabled: !isAdmin,
      },
      {
        key: 'edit',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => selectedId && navigate(editPath(entity, selectedId)),
        disabled: !isAdmin || !selectedId,
      },
      {
        key: 'delete',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: (ctx) => {
          if (!selectedId) return;
          const abortController = new AbortController();
          deleteAccessRule(abortController, entity, selectedId).then(() => {
            ctx.refresh();
            setSuccessMessage(`${entity} access rule ${selectedId} deleted.`);
          });
        },
        disabled: !isAdmin || !selectedId,
      },
    ],
    [entity, isAdmin, selectedId, navigate, setSuccessMessage]
  );

  return (
    <PagedDataGrid<IAccessRule>
      ref={gridRef}
      columns={COLUMNS}
      filterEnabled
      getAction={(
        abortController,
        pageSize,
        pageNo,
        orderBy,
        search,
        filters
      ) =>
        getAccessRules(
          abortController,
          entity,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        )
      }
      commandBarItems={commandBarItems}
      selectionMode="single"
      onSelectionChange={(selection) => setSelectedId(selection.id)}
      onRowDoubleClicked={(selection) =>
        navigate(editPath(entity, selection.id))
      }
    />
  );
};

export default AccessRulesList;
